import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import type { Control, UseFormClearErrors, UseFormSetValue } from 'react-hook-form';
import type { VehicleSearchFilters } from '@/features/vehiclesearch/types/manualsearch.types';
import type { PlanVehicleRuleFormInput } from '../schemas/planVehicleRule.schema';
import type {
    PlanVehicleRuleBatchFormInput,
    PlanVehicleRuleBatchFormOutput,
} from '../schemas/planVehicleRuleBatch.schema';
import { MAX_BATCH_RULES, countVehicleRuleSelections } from '../utils/expandVehicleRuleSelections';
import { usePlanVehicleRuleCascade } from './usePlanVehicleRuleCascade';

// The cascade's mount effect keys on this object's identity; a fresh literal per render would
// re-run the cascade on every state change.
const EMPTY_SEED_FILTERS: VehicleSearchFilters = {};

const isBlank = (value: unknown): boolean => value === '' || value === undefined || value === null;

interface Params {
    control: Control<PlanVehicleRuleBatchFormInput, unknown, PlanVehicleRuleBatchFormOutput>;
    setValue: UseFormSetValue<PlanVehicleRuleBatchFormInput>;
    clearErrors: UseFormClearErrors<PlanVehicleRuleBatchFormInput>;
}

export function usePlanVehicleRuleBatchSelections({ control, setValue, clearErrors }: Params) {
    // The cascade writes the single form's identity fields; only model and derivative exist here.
    const cascadeSetValue: UseFormSetValue<PlanVehicleRuleFormInput> = (name, value, options) => {
        if (name === 'model_id' || name === 'derivative_id') setValue(name, value as string, options);
    };

    const cascade = usePlanVehicleRuleCascade({
        seedFilters: EMPTY_SEED_FILTERS,
        mode: 'create',
        setValue: cascadeSetValue,
    });
    const { filters, handleFilterChange } = cascade;

    const values = useWatch({ control });
    const makeIds = values.make_ids ?? [];
    const transmissionIds = values.transmission_ids ?? [];
    const fuelTypeIds = values.fuel_type_ids ?? [];
    const derivativeSelected = Boolean(values.derivative_id);

    const singleMakeId = makeIds.length === 1 ? makeIds[0] : '';
    const hasSingleMake = singleMakeId !== '';
    const hasMakeAndModel = hasSingleMake && Boolean(filters.model_id);

    const handleMakesChange = (next: string[]) => {
        setValue('make_ids', next, { shouldDirty: true });
        const nextSingle = next.length === 1 ? next[0] : '';
        if (nextSingle !== (filters.make_id ?? '')) void handleFilterChange('make_id', nextSingle);
    };

    const handleModelChange = (modelId: string) => {
        void handleFilterChange('model_id', modelId);
    };

    useEffect(() => {
        if (derivativeSelected) {
            setValue('model_id', '');
            setValue('engine_min', '');
            setValue('engine_max', '');
            setValue('age_min', '');
            setValue('age_max', '');
            setValue('requires_4x4', '');
            // The range fields are now blank and disabled, so a failed range validation must not
            // leave its message on them.
            clearErrors(['engine_min', 'engine_max', 'age_min', 'age_max']);
        } else {
            setValue('model_id', filters.model_id ?? '');
        }
    }, [derivativeSelected, filters.model_id, setValue, clearErrors]);

    const count = countVehicleRuleSelections(
        makeIds.length,
        derivativeSelected ? 0 : transmissionIds.length,
        derivativeSelected ? 0 : fuelTypeIds.length,
    );
    const overCap = count > MAX_BATCH_RULES;
    const isRuleEmpty =
        makeIds.length === 0 && transmissionIds.length === 0 && fuelTypeIds.length === 0 && !values.requires_4x4 &&
        [values.model_id, values.derivative_id, values.engine_min, values.engine_max, values.age_min, values.age_max].every(isBlank);

    return {
        ...cascade,
        singleMakeId,
        hasSingleMake,
        hasMakeAndModel,
        derivativeSelected,
        handleMakesChange,
        handleModelChange,
        count,
        overCap,
        isRuleEmpty,
    };
}
